import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Typography } from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import { useT } from "../i18n/context";
import type { ChatMessage, CodeBlockRenderer } from "../types";
import MessageBubble from "./MessageBubble";
import SessionExitedNotice from "./SessionExitedNotice";

const { Text } = Typography;

interface MessageListProps {
  messages: ChatMessage[];
  agentName: string;
  firstItemIndex: number;
  loadingHistory: boolean;
  onStartReached: () => void;
  codeBlockRenderers?: Record<string, CodeBlockRenderer>;
  onCodeBlockRenderError?: (language: string, cause: Error) => void;
}

export interface MessageListHandle {
  scrollToBottom: () => void;
  isAtBottom: () => boolean;
}

const MessageList = forwardRef<MessageListHandle, MessageListProps>(function MessageList(
  {
    messages,
    agentName,
    firstItemIndex,
    loadingHistory,
    onStartReached,
    codeBlockRenderers,
    onCodeBlockRenderError,
  },
  ref,
) {
  const t = useT();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const atBottomRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  const [showNewContent, setShowNewContent] = useState(false);

  useImperativeHandle(ref, () => ({
    scrollToBottom: () => {
      setShowNewContent(false);
      atBottomRef.current = true;
      setAtBottom(true);
      // Virtuoso 虚拟列表渲染是异步的，单次 scrollTop 可能不够
      // 连续滚 3 次（0ms / 50ms / 150ms）确保到达真正底部
      const doScroll = () => {
        if (scrollerRef.current) {
          scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
      };
      doScroll();
      setTimeout(doScroll, 50);
      setTimeout(doScroll, 150);
    },
    isAtBottom: () => atBottomRef.current,
  }));

  const handleAtBottomChange = useCallback((isAtBottom: boolean) => {
    atBottomRef.current = isAtBottom;
    setAtBottom(isAtBottom);
    if (isAtBottom) setShowNewContent(false);
  }, []);

  // 流式光点动画样式注入（仅一次）
  useEffect(() => {
    const id = "lude-im-pulse-style";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = "@keyframes lude-im-pulse{0%,100%{opacity:1}50%{opacity:.4}}";
    document.head.appendChild(style);
  }, []);

  // 不在底部时，有新消息或内容变化 → 显示 floating button
  const prevMsgCountRef = useRef(messages.length);
  const prevContentLenRef = useRef(0);
  const lastMsg = messages[messages.length - 1];
  const contentLen = lastMsg?.content?.length ?? 0;
  useEffect(() => {
    const hasNew = messages.length > prevMsgCountRef.current || contentLen > prevContentLenRef.current;
    if (hasNew && !atBottomRef.current) {
      setShowNewContent(true);
    }
    prevMsgCountRef.current = messages.length;
    prevContentLenRef.current = contentLen;
  }, [messages.length, contentLen]);

  const lastId = messages[messages.length - 1]?.id;

  return (
    <div style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column" }}>
      <Virtuoso
        ref={virtuosoRef}
        scrollerRef={(el) => { scrollerRef.current = el as HTMLElement | null; }}
        style={{ flex: 1 }}
        data={messages}
        firstItemIndex={firstItemIndex}
        followOutput={(isAtBottom) => isAtBottom ? "smooth" : false}
        alignToBottom
        atBottomStateChange={handleAtBottomChange}
        atBottomThreshold={50}
        startReached={onStartReached}
        initialTopMostItemIndex={Math.max(messages.length - 1, 0)}
        components={{
          Header: () =>
            loadingHistory ? (
              <div style={{ textAlign: "center", padding: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("chat.loading")}
                </Text>
              </div>
            ) : null,
        }}
        itemContent={(_index, msg) => {
          if (msg.role === "system" && msg.systemKind === "exited") {
            return <SessionExitedNotice reason={msg.content || undefined} />;
          }
          return (
            <MessageBubble
              msg={msg}
              agentName={agentName}
              isLast={msg.id === lastId}
              codeBlockRenderers={codeBlockRenderers}
              onCodeBlockRenderError={onCodeBlockRenderError}
            />
          );
        }}
      />

      {/* 回到底部浮动按钮：不在底部时始终显示 */}
      {!atBottom && (
        <button
          onClick={() => {
            const doScroll = () => { if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight; };
            doScroll(); setTimeout(doScroll, 50); setTimeout(doScroll, 150);
            setShowNewContent(false);
            setAtBottom(true);
            atBottomRef.current = true;
          }}
          style={{
            position: "absolute",
            bottom: 12,
            right: 20,
            zIndex: 10,
            width: 36,
            height: 36,
            background: showNewContent ? "var(--lude-im-accent, #1677ff)" : "var(--lude-im-bg-elevated, #2f2f2f)",
            color: showNewContent ? "#fff" : "var(--lude-im-text-secondary, #a0a0a0)",
            border: showNewContent ? "none" : "1px solid var(--lude-im-border, #333)",
            borderRadius: "50%",
            padding: 0,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            transition: "background 0.2s, color 0.2s",
          }}
          title={showNewContent ? t("chat.new_content") : ""}
        >
          <ArrowDownOutlined />
        </button>
      )}
    </div>
  );
});

export default MessageList;
